/**
 * AdminRazorpayCard — admin kill-switch for live Razorpay payments + recent
 * payment intents view. Surfaces whether we're hitting live or test mode and
 * whether a webhook secret is configured.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Warning, CheckCircle, Spinner, FloppyDisk, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRazorpayCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/payments/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/payments/recent?limit=10`, { withCredentials: true }),
      ]);
      setSettings(s.data);
      setRecent(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Razorpay settings');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/payments/settings`,
        { enabled }, { withCredentials: true });
      setSettings(data);
      toast.success(enabled ? 'Payments enabled' : 'Payments disabled');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading || !settings) {
    return (
      <Card className="bg-white border-[#E8E1D5]">
        <CardContent className="py-6">
          <p className="text-sm text-text-secondary text-center">Loading Razorpay settings…</p>
        </CardContent>
      </Card>
    );
  }

  const live = settings.live_mode;
  const maskedKey = settings.key_id
    ? settings.key_id.slice(0, 12) + '…' + settings.key_id.slice(-4)
    : '—';

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-razorpay-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <CreditCard size={20} className="text-brand-primary" weight="fill" /> Razorpay Payments
        </CardTitle>
        <CardDescription>
          Manage the customer-checkout, joining-fee and vendor-advance flows. The kill-switch
          below disables all <code className="font-mono">/api/payments/*</code> endpoints instantly
          if anything looks off.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Live-mode banner */}
        {live ? (
          <div className="mb-4 p-3 border border-red-200 bg-red-50 flex items-start gap-2"
            data-testid="razorpay-live-banner">
            <Warning size={18} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
            <div className="text-xs text-red-700">
              <strong>LIVE MODE</strong> — every successful checkout debits a real card. Toggle
              payments off below if you need to pause refunds, investigate disputes, or test new flows.
              To switch to test mode, rotate <code className="font-mono">RAZORPAY_KEY_ID</code> to
              one starting with <code className="font-mono">rzp_test_</code>.
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 border border-emerald-200 bg-emerald-50 flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-700 shrink-0" />
            <div className="text-xs text-emerald-700">
              <strong>Test mode</strong> — no real money will move. Use card 4111 1111 1111 1111
              with any future expiry + CVV for test checkouts.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <div className="p-3 border border-[#E8E1D5] bg-sand-50">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary">Key ID</div>
            <div className="font-mono text-text-primary truncate" data-testid="razorpay-key-id">{maskedKey}</div>
          </div>
          <div className="p-3 border border-[#E8E1D5] bg-sand-50">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary">Webhook secret</div>
            <div className={`text-xs font-medium ${settings.webhook_secret_configured ? 'text-emerald-700' : 'text-amber-700'}`}
              data-testid="razorpay-webhook-status">
              {settings.webhook_secret_configured ? 'Configured ✓' : 'Not set — refunds & async updates skipped'}
            </div>
          </div>
        </div>

        {/* Kill-switch */}
        <div className="flex items-center justify-between p-3 border border-[#E8E1D5]">
          <div>
            <div className="text-sm font-medium">Payments enabled</div>
            <div className="text-[11px] text-text-secondary">
              When off, <code className="font-mono">create-order</code> and{' '}
              <code className="font-mono">verify-payment</code> return 503.
              The Razorpay checkout modal still renders client-side but cannot complete.
            </div>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={toggle} disabled={saving}
            data-testid="razorpay-enabled-switch" />
        </div>

        {/* Recent payments */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
              Recent payment intents (10)
            </div>
            <button onClick={load} className="text-xs text-brand-primary hover:underline flex items-center gap-1"
              data-testid="razorpay-refresh-btn">
              <ArrowsClockwise size={12} /> Refresh
            </button>
          </div>
          {recent.length === 0 ? (
            <p className="text-xs text-text-secondary italic">No payment intents yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E8E1D5] text-text-secondary text-left">
                    <th className="py-1.5 pr-3">Order ID</th>
                    <th className="py-1.5 pr-3">Amount</th>
                    <th className="py-1.5 pr-3">Purpose</th>
                    <th className="py-1.5 pr-3">Status</th>
                    <th className="py-1.5 pr-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={r.order_id || i} className="border-b border-[#F5EFE5] hover:bg-sand-50"
                      data-testid={`razorpay-payment-${i}`}>
                      <td className="py-1.5 pr-3 font-mono truncate max-w-[180px]">{r.order_id}</td>
                      <td className="py-1.5 pr-3">₹{(Number(r.amount || 0) / 100).toLocaleString('en-IN')}</td>
                      <td className="py-1.5 pr-3 text-text-secondary">{r.purpose || '—'}</td>
                      <td className="py-1.5 pr-3">
                        <span className={
                          r.status === 'paid' ? 'text-emerald-700 font-bold' :
                          r.status === 'failed' || r.status === 'signature_mismatch' ? 'text-red-600 font-bold' :
                          r.status === 'refunded' ? 'text-purple-700 font-bold' : 'text-text-secondary'
                        }>{r.status}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-text-secondary">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] text-text-secondary mt-4">
          Webhook URL: <code className="font-mono">/api/payments/razorpay/webhook</code> · Configure at{' '}
          <a href="https://dashboard.razorpay.com/app/webhooks" target="_blank" rel="noreferrer"
            className="text-brand-primary hover:underline">dashboard.razorpay.com/app/webhooks</a> and
          paste the generated secret into <code className="font-mono">RAZORPAY_WEBHOOK_SECRET</code>{' '}
          in <code className="font-mono">backend/.env</code>.
        </p>
      </CardContent>
    </Card>
  );
};

export default AdminRazorpayCard;
